import React from 'react';
import { observer } from 'mobx-react';
import { CircularProgress, Card, Grid } from '@material-ui/core';
import { connect } from 'formik';
import { getSnapshot } from 'mobx-state-tree';

import { useStore } from 'core/store';
import { useQuery, gql } from '@apollo/client';
import useErrors from 'common/hooks/use-errors';
import { PolicyErrors } from './components/policy-errors';
import { Additionals } from './components/additionals';
import { DiscountsCard } from './components/discounts-card';
import { AdditionalOffersSection } from './components/additional-offers-section';
import { PriceDetailsCard } from './components/price-details-card';
import { PolicyPickerCard } from './components/policy-picker-card';
import { Downloads } from './components/downloads';
import useStyles from './sidebar.styles';

const Sidebar = observer(() => {
  const classes = useStyles();
  const { offer: store } = useStore();
  const getErrorsAndRejections = (code) => code >= 4003;
  const errorsAndRejections = useErrors(getSnapshot(store.errors), getErrorsAndRejections);

  const assurityLifeEligibilityQuery = useQuery(
    gql`
      query ($offerId: String) {
        assurityLifeCheckEligibility(args: { branchOfferId: $offerId }) {
          __typename
          ... on AssurityLifeCheckEligibilitySuccessResponse {
            staffUserCapabilities
            eligibleLifeOffers {
              benefitAmount
              monthlyPremiumAmount
              annualPremiumAmount
              personId
            }
          }
        }
      }
    `,
    { variables: { offerId: store?.offer?.id }, skip: !store?.offer?.id }
  );

  const shouldShowAssurityTermLifeAdditionalOffer = Boolean(
    !assurityLifeEligibilityQuery.loading &&
      assurityLifeEligibilityQuery.data?.assurityLifeCheckEligibility.__typename !==
        'AssurityLifeCheckEligibilityStaffUserNotEligibleResponse'
  );

  const shouldShowAdditionalOffers = shouldShowAssurityTermLifeAdditionalOffer;

  const isLoading = store.loading || !store.offer || assurityLifeEligibilityQuery.loading;

  if (isLoading) {
    return (
      <>
        <div className={classes.columnHeader} />
        <Card className={classes.container}>
          <Grid container className={classes.loaderContainer} alignItems="center" justifyContent="center">
            <CircularProgress className={classes.loader} />
          </Grid>
        </Card>
      </>
    );
  }

  return (
    <div className={classes.sidebar}>
      <div className={classes.columnHeader} />
      <PolicyPickerCard />
      <PriceDetailsCard />
      {!store.isBranchPlusOnly && <DiscountsCard />}
      {store.shouldShowAdditionalPaymentsSection && <Additionals />}
      {shouldShowAdditionalOffers && (
        <AdditionalOffersSection
          assurityTermLife={shouldShowAssurityTermLifeAdditionalOffer}
          assurityTermLifeData={{
            ...assurityLifeEligibilityQuery.data?.assurityLifeCheckEligibility,
            branchOfferId: store?.offer?.id,
            email: store?.offer?.quote.email
          }}
        />
      )}
      {errorsAndRejections.length > 0 && <PolicyErrors />}
      <Downloads />
    </div>
  );
});

export default connect(Sidebar);
